import { type ReactNode } from 'react';
import {
  findOtherOption,
  formatMembershipProfileValue,
  isOtherChosen,
  isOtherTextMissing,
  membershipFieldStorageTarget,
  otherTextKey,
  resolveFieldDisplayStyle,
  type FieldDef,
  type MembershipFieldStorageTarget,
  type MembershipProfileValue,
} from '@citizen-walk/shared-types';
import { OtherTextInput, RatingScaleButtons } from '@citizen-walk/shared-ui';

/**
 * 地区別ユーザー登録フォーム — 共通コンポーネント (Sprint BG-DataSettings 4)。
 *
 * 旧 v1 `old/apps/user/src/components/UserFieldsForm.tsx` (272 行) を完全踏襲。
 *
 * 設計原則:
 * - 値も選択肢も地区ごとに完全独立
 * - District.dataSettings.userFields を唯一の真実の源として動的レンダリング
 * - 組み込み key (nickname/gender/ageGroup/relationship/attribute) もカスタム key も同一ロジックで描画
 * - sessions だけは特殊扱い (外側で SessionPicker を提供、renderSessions prop)
 * - photo / location はオンボーディング・設定画面では入力 UI を出さない (該当 type の row は skip)
 * - 選択肢「その他」の記入欄 (2026-10-06、`fieldOtherText.ts`): 記入内容は組込項目 (性別等) も含め
 *   custom (= profileFieldValues) の `{key}__other` に入れる。「その他」を外すと消える。
 */

export interface UserFieldsFormValues {
  /**
   * 組み込み key の値 (key === FieldDef.key)。ニックネーム以外の専用欄は、項目の型に応じて
   * 文字列 / 配列 (複数選択) / 数値 / 真偽値を持つ (2026-10-08、型を決め打ちしない)。
   * 未入力は '' (空文字) か null。
   */
  builtin: {
    nickname: string;
    gender: MembershipProfileValue;
    ageGroup: MembershipProfileValue;
    relationship: MembershipProfileValue;
    attribute: MembershipProfileValue;
  };
  /** 地区独自の追加質問の回答 (profileFieldValues に保存)。 */
  custom: Record<string, unknown>;
}

export const EMPTY_USER_FIELDS_VALUES: UserFieldsFormValues = {
  builtin: { nickname: '', gender: '', ageGroup: '', relationship: '', attribute: '' },
  custom: {},
};

export interface UserFieldsFormProps {
  fields: FieldDef[];
  values: UserFieldsFormValues;
  onChange: (next: UserFieldsFormValues) => void;
  /** sessions フィールドの代替として外側で描画するノード (onboarding のみ)。 */
  renderSessions?: () => ReactNode;
  /** 入力 disable (saving 中など)。 */
  disabled?: boolean;
}

export function UserFieldsForm({
  fields,
  values,
  onChange,
  renderSessions,
  disabled,
}: UserFieldsFormProps) {
  const ordered = [...fields]
    .filter((f) => f.mode !== 'hidden')
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

  function setBuiltin(column: keyof UserFieldsFormValues['builtin'], v: string) {
    onChange({ ...values, builtin: { ...values.builtin, [column]: v } });
  }

  function setCustom(key: string, v: unknown) {
    onChange({ ...values, custom: { ...values.custom, [key]: v } });
  }

  /** 項目の保存先 (専用欄 / 独自項目) に値を入れる。専用欄も型を問わず入れる (数値・真偽値等)。 */
  function setFieldValue(target: MembershipFieldStorageTarget, key: string, v: unknown) {
    if (target.kind === 'builtin') {
      onChange({ ...values, builtin: { ...values.builtin, [target.column]: v } });
    } else if (target.kind === 'custom') {
      setCustom(key, v);
    }
  }

  /**
   * select / multiselect の選択値を変える。組込項目は専用カラム、それ以外は custom に入れ、
   * 「その他」の記入内容 (常に custom) も同じ onChange で更新する (2 回呼ぶと片方が消えるため)。
   */
  function setChoice(
    field: FieldDef,
    target: MembershipFieldStorageTarget,
    next: string | string[],
    otherText?: string,
  ) {
    const builtin = { ...values.builtin };
    const custom = { ...values.custom };
    if (target.kind === 'builtin') {
      // 単一選択は文字列、複数選択は配列のまま専用欄に入れる (ニックネームは選択式にならない)。
      if (target.column !== 'nickname') builtin[target.column] = next;
    } else if (target.kind === 'custom') {
      custom[field.key] = next;
    }
    const k = otherTextKey(field.key);
    if (otherText !== undefined) custom[k] = otherText;
    const other = findOtherOption(field);
    // 消すときは key を落とさず '' にする (membership の保存は merge:true なので、key を落とすと
    // Firestore 側に前の記入内容が残る)。
    if ((!other || !isOtherChosen(other.value, next)) && k in custom) custom[k] = '';
    onChange({ builtin, custom });
  }

  /** 「その他」の記入欄。記入し始めたら「その他」を選んだ状態にする。 */
  function setOtherText(
    field: FieldDef,
    target: MembershipFieldStorageTarget,
    current: string | string[],
    otherValue: string,
    text: string,
  ) {
    let next = current;
    if (text !== '' && !isOtherChosen(otherValue, current)) {
      next = Array.isArray(current) ? [...current, otherValue] : otherValue;
    }
    setChoice(field, target, next, text);
  }

  function otherTextOf(field: FieldDef): string {
    const v = values.custom[otherTextKey(field.key)];
    return typeof v === 'string' ? v : '';
  }

  return (
    <div className="space-y-4">
      {ordered.map((field) => {
        const target = membershipFieldStorageTarget(field);

        if (target.kind === 'sessions') {
          return renderSessions ? <div key={field.key}>{renderSessions()}</div> : null;
        }

        const required = field.mode === 'required';
        const labelNode = (
          <label className="block mb-1 font-medium text-sm" htmlFor={`uf-${field.key}`}>
            {field.label} {required && <span className="text-danger-600">*</span>}
          </label>
        );
        const description = field.description ? (
          <p className="text-xs text-slate-500 mb-1">{field.description}</p>
        ) : null;

        const value: unknown =
          target.kind === 'builtin' ? values.builtin[target.column] : values.custom[field.key];
        // 文字の入力欄に出す値。項目の型を後から変えた場合 (複数選択→文字 等) も文字にして見せる。
        const textValue = typeof value === 'string' ? value : formatMembershipProfileValue(value);

        const onChangeStr = (v: string) =>
          target.kind === 'builtin' ? setBuiltin(target.column, v) : setCustom(field.key, v);

        // docs/105 §①: 表示スタイルを解決 (PostExtraFieldsForm と同一。userFields でも displayStyle を尊重)。
        const style = resolveFieldDisplayStyle(field);

        if (field.type === 'text') {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              {field.key === 'nickname' && (
                <p className="text-xs text-warning-700 mb-1">※ 個人情報は入力しないでください。</p>
              )}
              <input
                id={`uf-${field.key}`}
                type="text"
                value={textValue}
                onChange={(e) => onChangeStr(e.target.value)}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'textarea') {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <textarea
                id={`uf-${field.key}`}
                value={textValue}
                onChange={(e) => onChangeStr(e.target.value)}
                disabled={disabled}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'select') {
          const strVal = typeof value === 'string' ? value : '';
          const other = findOtherOption(field);
          const pick = (v: string) => setChoice(field, target, v);
          const otherInput = (inline: boolean) =>
            other ? (
              <OtherTextInput
                inline={inline}
                value={otherTextOf(field)}
                onChange={(t) => setOtherText(field, target, strVal, other.value, t)}
                ariaLabel={`${field.label}（${other.label}の内容）`}
                disabled={disabled}
              />
            ) : null;
          // docs/105 §①: displayStyle で UI 切替 (buttons / radio / dropdown)。PostExtraFieldsForm と同仕様。
          if (style === 'buttons') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div role="radiogroup" className="flex flex-wrap items-center gap-2">
                  {field.options.map((opt) => {
                    const active = strVal === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={disabled}
                        onClick={() => pick(active ? '' : opt.value)}
                        className={`min-h-touch px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {other && strVal === other.value && otherInput(false)}
              </div>
            );
          }
          if (style === 'radio' || style === 'checkboxes') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="grid gap-1 border border-slate-200 rounded-md p-3">
                  {field.options.map((opt) => {
                    const checked = strVal === opt.value;
                    const radio = (
                      <label key={opt.value} className="flex items-center gap-2 py-1 shrink-0">
                        <input
                          type="radio"
                          name={`uf-${field.key}`}
                          checked={checked}
                          onChange={() => pick(opt.value)}
                          disabled={disabled}
                          className="w-5 h-5"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    );
                    if (other && opt.value === other.value) {
                      return (
                        <div key={opt.value} className="flex items-center gap-2">
                          {radio}
                          {otherInput(true)}
                        </div>
                      );
                    }
                    return radio;
                  })}
                </div>
              </div>
            );
          }
          // default: dropdown
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <select
                id={`uf-${field.key}`}
                value={strVal}
                onChange={(e) => pick(e.target.value)}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100"
              >
                <option value="">未選択</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {other && strVal === other.value && otherInput(false)}
            </div>
          );
        }

        if (field.type === 'multiselect') {
          // 文字列 1 つで保存されている値 (単一選択から複数選択に変えた項目の旧回答) は 1 件の選択として扱う。
          const arr = Array.isArray(value)
            ? value.filter((x): x is string => typeof x === 'string')
            : typeof value === 'string' && value !== ''
              ? [value]
              : [];
          // 専用欄 (関係 等) も独自項目も、選んだ値の配列をそのまま保存する (2026-10-08)。
          const setMulti = (next: string[]) => setChoice(field, target, next);
          const other = findOtherOption(field);
          const otherChosen = other !== null && arr.includes(other.value);
          const otherInput = (inline: boolean) =>
            other ? (
              <OtherTextInput
                inline={inline}
                value={otherTextOf(field)}
                onChange={(t) => setOtherText(field, target, arr, other.value, t)}
                ariaLabel={`${field.label}（${other.label}の内容）`}
                disabled={disabled}
              />
            ) : null;

          const toggle = (optValue: string, on: boolean) =>
            setMulti(on ? [...arr, optValue] : arr.filter((x) => x !== optValue));
          // docs/105 §①: displayStyle で UI 切替 (buttons / dropdown / checkboxes)。PostExtraFieldsForm と同仕様。
          if (style === 'buttons') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <div className="flex flex-wrap items-center gap-2">
                  {field.options.map((opt) => {
                    const active = arr.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        aria-pressed={active}
                        disabled={disabled}
                        onClick={() => toggle(opt.value, !active)}
                        className={`min-h-touch px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {otherChosen && otherInput(false)}
              </div>
            );
          }
          if (style === 'dropdown') {
            return (
              <div key={field.key}>
                {labelNode}
                {description}
                <select
                  id={`uf-${field.key}`}
                  multiple
                  value={arr}
                  onChange={(e) =>
                    setMulti(Array.from(e.target.selectedOptions).map((o) => o.value))
                  }
                  disabled={disabled}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-100"
                  size={Math.min(6, field.options.length)}
                >
                  {field.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {otherChosen && otherInput(false)}
              </div>
            );
          }
          // default: checkboxes
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <div className="grid gap-1 border border-slate-200 rounded-md p-3">
                {field.options.map((opt) => {
                  const checked = arr.includes(opt.value);
                  const box = (
                    <label key={opt.value} className="flex items-center gap-2 py-1 shrink-0">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggle(opt.value, e.target.checked)}
                        disabled={disabled}
                        className="w-5 h-5"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  );
                  if (other && opt.value === other.value) {
                    return (
                      <div key={opt.value} className="flex items-center gap-2">
                        {box}
                        {otherInput(true)}
                      </div>
                    );
                  }
                  return box;
                })}
              </div>
            </div>
          );
        }

        if (field.type === 'number') {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <input
                id={`uf-${field.key}`}
                type="number"
                value={typeof value === 'number' ? value : ''}
                onChange={(e) => {
                  const n = e.target.value === '' ? null : Number(e.target.value);
                  setFieldValue(target, field.key, n);
                }}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'rating-scale') {
          const min = field.optionsMeta?.min ?? -3;
          const max = field.optionsMeta?.max ?? 3;
          const excludeZero = field.optionsMeta?.excludeZero === true;
          const posLabel = field.optionsMeta?.scaleLabels?.posLabel;
          const negLabel = field.optionsMeta?.scaleLabels?.negLabel;
          const steps: number[] = [];
          for (let n = min; n <= max; n++) {
            if (excludeZero && n === 0) continue;
            steps.push(n);
          }
          const selected = typeof value === 'number' ? value : undefined;
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <RatingScaleButtons
                steps={steps}
                selected={selected}
                onSelect={(n) => setFieldValue(target, field.key, n)}
                negLabel={negLabel}
                posLabel={posLabel}
                disabled={disabled}
              />
            </div>
          );
        }

        if (field.type === 'date') {
          return (
            <div key={field.key}>
              {labelNode}
              {description}
              <input
                id={`uf-${field.key}`}
                type="date"
                value={textValue}
                onChange={(e) => onChangeStr(e.target.value)}
                disabled={disabled}
                className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
              />
            </div>
          );
        }

        if (field.type === 'checkbox') {
          return (
            <div key={field.key}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(e) => setFieldValue(target, field.key, e.target.checked)}
                  disabled={disabled}
                  className="w-5 h-5"
                />
                <span className="text-sm">
                  {field.label} {required && <span className="text-danger-600">*</span>}
                </span>
              </label>
              {description}
            </div>
          );
        }

        // photo / location は UserFieldsForm では入力 UI を出さない (旧 v1 と同じ仕様)
        return null;
      })}
    </div>
  );
}

/**
 * 必須フィールドが未入力でないかチェック (旧 v1 `findMissingRequired` 踏襲)。
 * 戻り値: 不足している field の label 配列 (空なら全て満たす)。
 *
 * photo / location / sessions は本 form 外で扱うため skip。
 */
export function findMissingRequired(fields: FieldDef[], values: UserFieldsFormValues): string[] {
  const missing: string[] = [];
  for (const f of fields) {
    if (f.mode !== 'required') continue;
    if (f.type === 'photo' || f.type === 'location') continue;
    if (f.key === 'sessions') continue;
    const target = membershipFieldStorageTarget(f);
    let v: unknown;
    if (target.kind === 'builtin') v = values.builtin[target.column];
    else if (target.kind === 'custom') v = values.custom[f.key];
    if (v === undefined || v === null || v === '') {
      missing.push(f.label);
    } else if (Array.isArray(v) && v.length === 0) {
      missing.push(f.label);
    } else if (isOtherTextMissing(f, values.custom, v)) {
      // 「その他」を選んだのに記入が空 (記入内容は組込項目でも custom 側)。
      missing.push(`${f.label}（${findOtherOption(f)?.label ?? 'その他'}の内容）`);
    }
  }
  return missing;
}
